import { FormEvent, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

type WorkspaceKey = {
  provider: string;
  key_hint: string;
  valid: number;
  tested_at: string | null;
  updated_at: string;
};

type KeyStatus = {
  configured: boolean;
  key: WorkspaceKey | null;
  fallback_system_key: boolean;
};

export default function AdminAi() {
  const { user } = useAuth();
  const [status, setStatus] = useState<KeyStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [apiKey, setApiKey] = useState('');

  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/" replace />;

  async function load() {
    setLoading(true);
    try {
      const r = await api.get<KeyStatus>('/admin/api-keys/anthropic');
      setStatus(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function save(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (!apiKey.trim()) {
      setError('Clé requise.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/admin/api-keys/anthropic', { api_key: apiKey.trim() });
      setApiKey('');
      setShowEditor(false);
      setInfo('Clé enregistrée. Pense à la tester.');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sauvegarde impossible');
    } finally {
      setBusy(false);
    }
  }

  async function testKey() {
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      const r = await api.post<{ ok: boolean; detail: string | null }>(
        '/admin/api-keys/anthropic/test'
      );
      if (r.ok) setInfo('✅ Clé valide — l\'API a répondu correctement.');
      else setError(`Clé invalide. ${r.detail ?? ''}`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Test impossible');
    } finally {
      setBusy(false);
    }
  }

  async function deleteKey() {
    if (!confirm('Supprimer la clé enregistrée pour ce workspace ?')) return;
    setBusy(true);
    try {
      await api.del('/admin/api-keys/anthropic');
      setInfo('Clé supprimée. Le portail utilisera la clé système si elle est configurée.');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Suppression impossible');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-header">
        <h1>Configuration IA</h1>
      </div>

      {error && <div className="error-msg">{error}</div>}
      {info && (
        <div
          style={{
            padding: 10,
            background: 'var(--c-nav)',
            borderRadius: 8,
            marginBottom: 12,
          }}
        >
          {info}
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">Clé API Anthropic</div>
        <div className="card-meta" style={{ marginBottom: 12 }}>
          La clé est stockée chiffrée (AES-256-GCM) côté serveur. Seul le hint est visible. Si
          aucune clé workspace n'est définie, le Worker utilise la clé système configurée via{' '}
          <code>wrangler secret put ANTHROPIC_API_KEY</code>.
        </div>
        {loading && <div>Chargement…</div>}
        {!loading && status && (
          <>
            {status.configured && status.key ? (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto auto auto',
                  gap: 8,
                  alignItems: 'center',
                }}
              >
                <div>
                  <div style={{ fontFamily: 'monospace' }}>{status.key.key_hint}</div>
                  <div className="card-meta" style={{ fontSize: 11 }}>
                    {status.key.valid ? '✅ valide' : '⚠ invalide'} · maj{' '}
                    {status.key.updated_at?.slice(0, 16)}
                    {status.key.tested_at
                      ? ` · testée le ${status.key.tested_at.slice(0, 16)}`
                      : ' · jamais testée'}
                  </div>
                </div>
                <button className="btn btn-ghost" onClick={testKey} disabled={busy}>
                  Tester
                </button>
                <button
                  className="btn btn-ghost"
                  onClick={() => setShowEditor((v) => !v)}
                  disabled={busy}
                >
                  Modifier
                </button>
                <button className="btn btn-revoir" onClick={deleteKey} disabled={busy}>
                  Supprimer
                </button>
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div className="card-meta">
                  Aucune clé workspace.{' '}
                  {status.fallback_system_key
                    ? 'Clé système active (env.ANTHROPIC_API_KEY) — l\'IA fonctionne.'
                    : 'Pas de clé système non plus — l\'IA est indisponible.'}
                </div>
                <button className="btn btn-primary" onClick={() => setShowEditor(true)}>
                  Saisir une clé
                </button>
              </div>
            )}

            {showEditor && (
              <form onSubmit={save} style={{ marginTop: 14 }}>
                <div className="field">
                  <label>Clé API Anthropic</label>
                  <input
                    type="password"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="sk-ant-api03-..."
                    autoComplete="off"
                    required
                    style={{ fontFamily: 'monospace' }}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => {
                      setShowEditor(false);
                      setApiKey('');
                    }}
                  >
                    Annuler
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={busy}>
                    {busy ? 'Enregistrement…' : 'Enregistrer'}
                  </button>
                </div>
              </form>
            )}
          </>
        )}
      </div>

      <div className="card">
        <div className="card-title">Modèles utilisés</div>
        <div className="card-meta">
          Le portail sélectionne automatiquement le modèle adapté à chaque action :
        </div>
        <ul style={{ marginTop: 8, fontSize: 13 }}>
          <li>
            <b>Haiku 4.5</b> — extraction (dates, métadonnées, références), reformulation courte
            (résumés, mails, relances), analyse d'un document, régénération de section, ping de
            test API.
          </li>
          <li>
            <b>Sonnet 4.6</b> — raisonnement (décisions, risques, conformité SIA/ISO,
            comparaison de versions), traduction, composition longue (CR de réunion, présentations,
            checklists, récits hebdo).
          </li>
        </ul>
      </div>
    </>
  );
}
